import { useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { AlertOctagon } from "lucide-react";
import { api } from "../lib/api";
import FlowNode from "./FlowNode";
import BomEditor from "./BomEditor";
import BomDetailCard from "./BomDetailCard";
import RoutedEdge from "./RoutedEdge";
import { findCycle, cycleEdgeIds, autoLayout, upstreamTotals } from "../lib/graph";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const nodeTypes = { flow: FlowNode };
const edgeTypes = { routed: RoutedEdge };

function useLineage(rootId) {
  const [g, setG] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { if (rootId) { api.get("/bom-graph").then((r) => setG(r.data)); } }, [rootId, tick]);
  const lineage = useMemo(() => {
    if (!g) return null;
    const totals = upstreamTotals(rootId, g.edges);
    const nodes = g.nodes.filter((n) => totals[n.id] != null);
    const edges = g.edges.filter((e) => totals[e.source] != null && totals[e.target] != null);
    return { nodes, edges, totals, chain: findCycle(edges) };
  }, [g, rootId]);
  return { lineage, reload: () => setTick((t) => t + 1) };
}

const Legend = () => (
  <div className="flex flex-wrap gap-4 font-mono text-[10px] text-[#9CA3AF]" data-testid="flowchart-legend">
    <span><span className="inline-block w-2 h-2 rounded-sm bg-[#EF4444] mr-1" />SHORTAGE / LOOP</span>
    <span><span className="inline-block w-2 h-2 rounded-sm bg-[#F59E0B] mr-1" />DELAY / LABOUR NOT SET</span>
    <span>TAP A NODE TO OPEN ITS FACT SHEET</span>
  </div>
);

export default function BomFlowchartModal({ item, onClose, onOpenItem }) {
  const { lineage: data, reload } = useLineage(item?.id);
  const [createFor, setCreateFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const view = useMemo(() => {
    if (!data) return null;
    const { pos, stages, routes, nodeWidth } = autoLayout(data.nodes, data.edges);
    const loop = new Set(data.chain || []);
    const bad = cycleEdgeIds(data.chain);
    const nodes = data.nodes.map((n) => ({ id: n.id, type: "flow", position: pos[n.id], draggable: false,
      data: { node: n, stage: stages[n.id], overlay: true, inCycle: loop.has(n.id), highlight: n.id === item.id, onCreateBom: setCreateFor, onOpenBom: setDetailFor, total: n.id !== item.id ? data.totals[n.id] : null } }));
    const edges = data.edges.map((e) => { const c = bad.has(e.id) ? "#EF4444" : "#F59E0B";
      return { id: e.id, type: "routed", data: { points: routes[e.id], width: nodeWidth }, source: e.source, target: e.target, label: `×${e.qty}`, animated: bad.has(e.id), style: { stroke: c, strokeWidth: 2 },
        labelStyle: { fill: "#E5E7EB", fontFamily: "monospace", fontSize: 11 }, labelBgStyle: { fill: "#0F1623" }, markerEnd: { type: MarkerType.ArrowClosed, color: c } }; });
    return { nodes, edges };
  }, [data, item]);
  if (!item) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-[96vw] w-[96vw] h-[90vh] flex flex-col" data-testid="bom-flowchart-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">BOM Flowchart · {item.name}</DialogTitle>
          <DialogDescription className="text-[#9CA3AF] font-mono text-xs">{item.sku} · upstream lineage, quantities per 1 {item.unit}</DialogDescription>
        </DialogHeader>
        <Legend />
        {data?.chain && <div data-testid="flowchart-recursion-warning" className="flex items-center gap-2 p-3 rounded-md border-2 border-[#EF4444] bg-[#1A0B0E] text-sm text-white">
          <AlertOctagon className="w-4 h-4 text-[#EF4444]" />Loop Detected: {data.chain.map((i) => data.nodes.find((n) => n.id === i)?.sku || "?").join(" → ")}</div>}
        <div className="relative flex-1 min-h-0 rounded-md border border-[#1E293B] bg-[#090D16] overflow-hidden" data-testid="bom-flowchart-canvas">
          {view && (data.edges.length ? (
            <ReactFlow nodes={view.nodes} edges={view.edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} nodesConnectable={false} colorMode="dark" fitView minZoom={0.15}
              onNodeClick={(_, n) => onOpenItem(n.id)} proOptions={{ hideAttribution: true }}>
              <Background color="#1E293B" gap={24} /><Controls showInteractive={false} />
            </ReactFlow>
          ) : <div className="h-full grid place-items-center text-[#6B7280]" data-testid="flowchart-empty">
            <div className="flex flex-col items-center gap-3">No BOM linked to this item yet.
              <button data-testid="flowchart-empty-create-bom" onClick={() => setCreateFor(item.id)} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold">Create BOM</button>
            </div></div>)}
          {detailFor && <BomDetailCard productId={detailFor} onClose={() => setDetailFor(null)} />}
        </div>
        {createFor && <BomEditor mode="new" bom={null} presetProductId={createFor} onClose={() => { setCreateFor(null); reload(); }} />}
      </DialogContent>
    </Dialog>
  );
}
